import { Injectable } from '@angular/core';

import { NEVER, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, mapTo, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';
import { ApiQuery } from '../../core/api/entities/api-query.entity';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { FragmentId, Identifier, Metadata } from '../../core/records/entities/metadata/metadata.entity';
import { ShRecordService } from '../../core/records/services/record.service';
import { GenerateGuid } from '../../helpers/guid-generator.helper';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { QueryIterator } from '../entities/query-iterator.entity';
import { AbstractRecordsStateService } from './records-state.abstract.service';

interface InferredParams {
    readonly totalNumberOfResults: number;
}

interface QueryState {
    iterator: QueryIterator;
    params?: InferredParams;
    pageCache: PageCache;
}

interface Cursor<T> {
    current: T;
    next?: T;
    prev?: T;
}

export interface ElementReference {
    id: FragmentId;
    page: number;
}

export type ActiveElementReference = Cursor<ElementReference>;

interface IdList {
    ids: FragmentId[];
}

type PageCache = Map<number, IdList>;

@Injectable({ providedIn: 'root' })
export class QueryStateHandler {
    protected readonly queryStates: Map<string, QueryState>;
    protected readonly activeRefs: Map<string, ReplaySubject<ActiveElementReference | undefined>>;

    constructor(
        protected recordsState: AbstractRecordsStateService,
        protected search: ShRecordService
    ) {
        this.queryStates = new Map();
        this.activeRefs = new Map();
    }

    /* #region Creation and removal  */
    public create({ sortInfo, query, resultsPerPage }: Omit<QueryIterator, 'id'>): QueryIterator {
        const id: string = GenerateGuid();
        const iterator: QueryIterator = {
            id,
            sortInfo,
            query,
            resultsPerPage
        };
        const queryState: QueryState = {
            iterator,
            pageCache: new Map()
        };

        this.activeRefs.set(id, new ReplaySubject(1));
        this.queryStates.set(id, queryState);

        return iterator;
    }
    /* #endregion */

    /* #region Active element*/

    public setActive(queryIteratorId: string, identifier: FragmentId, page?: number): void {
        const idpage: number | undefined = page ?? this.findPageOfIdentifier(queryIteratorId, identifier);

        if (typeof idpage === 'undefined') {
            console.error(
                `Expected to find ${identifier} in the cache of ${queryIteratorId}, but did not. Unable to set active`
            );
            return;
        }
        this.setActiveOnPage(queryIteratorId, identifier, idpage);
    }

    public getActive(queryIteratorId: string): Observable<ActiveElementReference | undefined> {
        return this.getActiveRef(queryIteratorId).asObservable();
    }

    private setActiveOnPage(queryIteratorId: string, identifier: FragmentId, page: number): void {
        const loadedPage: IdList | undefined = this.getCachedPage(queryIteratorId, page);
        if (loadedPage === undefined || loadedPage.ids.length === 0) {
            console.error(`Page ${page} in cache is empty, which was not expected at this point.`);
            return;
        }

        const index: number = loadedPage.ids.indexOf(identifier);
        if (index < 0) {
            console.error(`Expected to find ${identifier} on page ${page}, but did not.`);
            return;
        }

        this.getActiveElementReference(queryIteratorId, identifier, page, index, loadedPage.ids).subscribe(ref =>
            this.getActiveRef(queryIteratorId).next(ref)
        );
    }

    private getActiveElementReference(
        queryIteratorId: string,
        identifier: FragmentId,
        page: number,
        index: number,
        ids: FragmentId[]
    ): Observable<ActiveElementReference> {
        const baseActiveElement: ActiveElementReference = {
            next: undefined,
            prev: undefined,
            current: { id: identifier, page }
        };

        if (index === 0) {
            // --> start of range
            const activeElement: ActiveElementReference = {
                ...baseActiveElement,
                next: ids[index + 1]
                    ? {
                          id: ids[index + 1],
                          page
                      }
                    : undefined
            };

            if (!this.pageInRange(queryIteratorId, page - 1)) {
                return of(activeElement);
            }
            return this.ensurePageLoaded(queryIteratorId, page - 1).pipe(
                switchMap(() => {
                    // get previous page ids
                    const loadedPage: IdList | undefined = this.getCachedPage(queryIteratorId, page - 1);
                    if (loadedPage === undefined || loadedPage.ids.length === 0) {
                        return of(activeElement);
                    }
                    const loadedIds: FragmentId[] = loadedPage.ids;
                    const foundIndex: number = loadedIds.indexOf(identifier);
                    if (foundIndex > -1) {
                        return this.getActiveElementReference(
                            queryIteratorId,
                            identifier,
                            page + 1,
                            foundIndex,
                            loadedIds
                        );
                    }
                    return of({ ...activeElement, prev: { id: loadedIds[loadedIds.length - 1], page: page - 1 } });
                })
            );
        } else if (index + 1 === ids.length) {
            // --> end of range

            const activeElement: ActiveElementReference = { ...baseActiveElement, prev: { id: ids[index - 1], page } };

            if (!this.pageInRange(queryIteratorId, page + 1)) {
                return of(activeElement);
            }
            return this.ensurePageLoaded(queryIteratorId, page + 1).pipe(
                switchMap(() => {
                    // get next page ids
                    const loadedPage: IdList | undefined = this.getCachedPage(queryIteratorId, page + 1);
                    if (loadedPage === undefined || loadedPage.ids.length === 0) {
                        return of(activeElement);
                    }
                    const loadedIds: FragmentId[] = loadedPage.ids;

                    const foundIndex: number = loadedIds.indexOf(identifier);
                    if (foundIndex > -1) {
                        return this.getActiveElementReference(
                            queryIteratorId,
                            identifier,
                            page + 1,
                            foundIndex,
                            loadedIds
                        );
                    }

                    return of({ ...activeElement, next: { id: loadedIds[0], page: page + 1 } });
                }),
                startWith(activeElement)
            );
        } else {
            const activeElement: ActiveElementReference = {
                ...baseActiveElement,
                next: { id: ids[index + 1], page },
                prev: { id: ids[index - 1], page }
            };
            return of(activeElement);
        }
    }
    /* #endregion */

    /* #region Utility methods*/

    private getQueryState(queryIteratorId: string): QueryState {
        const pageCache: QueryState | undefined = this.queryStates.get(queryIteratorId);
        if (typeof pageCache === 'undefined') {
            throw new Error(`Couldn't get QueryState: queryIteratorId ${queryIteratorId} does not exist`);
        }
        return pageCache;
    }

    private getPageCache(queryIteratorId: string): PageCache {
        const pageCache: PageCache | undefined = this.getQueryState(queryIteratorId).pageCache;
        if (typeof pageCache === 'undefined') {
            throw new Error(`Couldn't get PageCache: queryIteratorId ${queryIteratorId} does not exist`);
        }
        return pageCache;
    }

    private getQueryIterator(queryIteratorId: string): QueryIterator {
        const iterator: QueryIterator | undefined = this.getQueryState(queryIteratorId).iterator;
        if (typeof iterator === 'undefined') {
            throw new Error(`Couldn't get QueryIterator: queryIteratorId ${queryIteratorId} does not exist`);
        }
        return iterator;
    }

    private getActiveRef(queryIteratorId: string): Subject<ActiveElementReference | undefined> {
        const activeRef: Subject<ActiveElementReference | undefined> | undefined = this.activeRefs.get(queryIteratorId);
        if (typeof activeRef === 'undefined') {
            throw new Error(`Couldn't get activeRef: queryIteratorId ${queryIteratorId} does not exist`);
        }
        return activeRef;
    }

    private getInferredParams(queryIteratorId: string): InferredParams {
        const params: InferredParams | undefined = this.getQueryState(queryIteratorId).params;
        if (typeof params === 'undefined') {
            throw new Error(`Couldn't get QueryIterator: queryIteratorId ${queryIteratorId} does not exist`);
        }
        return params;
    }

    private setInferredParams(queryIteratorId: string, params: InferredParams): void {
        const existingParams: InferredParams | undefined = this.queryStates.get(queryIteratorId)?.params;
        const state: QueryState = this.getQueryState(queryIteratorId);
        const newState: QueryState = {
            ...state,
            params: {
                ...existingParams,
                ...params
            }
        };
        this.queryStates.set(queryIteratorId, newState);
    }
    /* #endregion */

    /* #region public methods */
    private hasPage(queryIteratorId: string, page: number): boolean {
        return this.getPageCache(queryIteratorId).has(page);
    }

    public getPage(
        queryIteratorId: string,
        page: number,
        setLoading: boolean = true
    ): Observable<PaginatedResults<ApiFragment>> {
        this.resetError();
        if (this.hasPage(queryIteratorId, page)) {
            return this.retrieveCachedPage(queryIteratorId, page);
        }

        if (setLoading) {
            this.setLoading(true);
        }

        return this.loadPage(queryIteratorId, page).pipe(
            tap(data => {
                this.setInferredParams(queryIteratorId, {
                    totalNumberOfResults: data.paginationInfo.totalNumberOfResults
                });
                this.addPage(queryIteratorId, page, data.results);
                this.setLoading(false);
            })
        );
    }

    public getRecord(activeElementRef: ActiveElementReference): Observable<ApiFragment> {
        // TODO: handle case when this is not present in cache.
        return this.recordsState.getDefinedRecords([activeElementRef.current.id]).pipe(map(list => list[0]));
    }

    public registerConsultation(fragmentId: FragmentId): void {
        this.search.registerConsultation(fragmentId);
    }

    /* #endregion */

    /* #region Page handling*/

    protected handleError(error: Error): typeof NEVER {
        this.recordsState.setRecordsError(true);
        this.setLoading(false);
        return NEVER;
    }

    protected resetError(): void {
        this.recordsState.setRecordsError(false);
    }

    protected getCachedPage(queryIteratorId: string, page: number): IdList {
        const idList: IdList | undefined = this.getPageCache(queryIteratorId).get(page);
        if (!idList) {
            throw new Error(
                `Page defined by queryIteratorId ${queryIteratorId} and page ${page} does not exist in cache`
            );
        }
        return idList;
    }

    protected clearAllPages(queryIteratorId: string): void {
        this.queryStates.delete(queryIteratorId);
    }

    protected findPageOfIdentifier(queryIteratorId: string, identifier: FragmentId): number | undefined {
        for (const [pageNumber, value] of this.getPageCache(queryIteratorId).entries()) {
            if (value.ids.includes(identifier)) {
                return pageNumber;
            }
        }
        return undefined;
    }

    protected ensurePageLoaded(queryIteratorId: string, page: number): Observable<boolean> {
        if (!this.hasPage(queryIteratorId, page)) {
            return this.loadPage(queryIteratorId, page).pipe(
                tap(data => {
                    this.setInferredParams(queryIteratorId, {
                        totalNumberOfResults: data.paginationInfo.totalNumberOfResults
                    });
                    this.addPage(queryIteratorId, page, data.results);
                }),
                mapTo(true)
            );
        } else {
            return of(true);
        }
    }

    protected pageInRange(queryIteratorId: string, page: number): boolean {
        const params: InferredParams = this.getInferredParams(queryIteratorId);
        const iterator: QueryIterator = this.getQueryIterator(queryIteratorId);

        return page >= 0 && page * iterator.resultsPerPage < params.totalNumberOfResults;
    }

    protected setLoading(loading = true): void {
        this.recordsState.setRecordsLoading(loading);
    }

    public getLoading(): Observable<boolean> {
        return this.recordsState.getRecordsLoading();
    }

    public getError(): Observable<boolean> {
        return this.recordsState.getRecordsError();
    }

    protected addPage(queryIteratorId: string, page: number, records: ApiFragment[]): void {
        const ids: Identifier[] = records.map(entity => entity.getIdentifier());

        this.clearPagesIfOverlap(queryIteratorId, page - 1, ids);
        this.clearPagesIfOverlap(queryIteratorId, page + 1, ids);

        this.getPageCache(queryIteratorId).set(page, { ids: records.map(entity => entity.getFragmentId()) });

        // MED=1390: Records are made immutable after adding to state, causing saving etc to fail
        this.recordsState.addRecords(cloneDeep(records));
    }

    protected clearPagesIfOverlap(queryIteratorId: string, page: number, arr: Identifier[]): void {
        if (this.hasPageOverlap(queryIteratorId, page, arr)) {
            this.clearAllPages(queryIteratorId);
        }
    }

    protected hasPageOverlap(queryIteratorId: string, page: number, arr: Identifier[]): boolean {
        if (!this.getPageCache(queryIteratorId).has(page)) {
            return false;
        }

        const pageIds: Identifier[] = this.getCachedPage(queryIteratorId, page).ids;

        return this.hasOverlap(arr, pageIds);
    }

    protected hasOverlap<T>(arr1: T[], arr2: T[]): boolean {
        return arr1.some(el => arr2.includes(el));
    }

    protected retrieveCachedPage(queryIteratorId: string, page: number): Observable<PaginatedResults<ApiFragment>> {
        const iterator: QueryIterator = this.getQueryIterator(queryIteratorId);
        const params: InferredParams = this.getInferredParams(queryIteratorId);
        const idList: IdList = this.getCachedPage(queryIteratorId, page);

        return this.recordsState.getDefinedRecords(idList.ids).pipe(
            map(results => ({
                paginationInfo: {
                    totalNumberOfResults: params.totalNumberOfResults,
                    page,
                    resultsPerPage: iterator.resultsPerPage
                },
                results
            }))
        );
    }

    protected loadPage(queryIteratorId: string, page: number): Observable<PaginatedResults<ApiFragment>> {
        const iterator: QueryIterator = this.getQueryIterator(queryIteratorId);

        const paginationInfo: PaginationInfo = {
            page,
            resultsPerPage: iterator.resultsPerPage,
            totalNumberOfResults: 0
        };
        const query: ApiQuery = iterator.query;

        return this.search.getRecords(paginationInfo, iterator.sortInfo, query).pipe(
            map(paginatedResults => ({
                ...paginatedResults,
                results: paginatedResults.results.map(record => new ApiFragment(record.metadata as Metadata))
            })),
            catchError(this.handleError.bind(this)),
            shareReplay(1)
        );
    }
    /* #endregion */
}
